import type { SessionInput, AIAction } from '@/types/api';

/**
 * Represents a grouped section of conversation:
 * - One user input (prompt + ad-hoc context)
 * - Multiple assistant responses (AI actions/explanations)
 */
export interface ConversationSection {
  id: number;
  userInput: SessionInput;
  assistantResponses: SessionInput[];
  isSummary?: boolean;
}

/**
 * Groups session inputs into conversation sections.
 * Each section starts with a user input and includes all following
 * assistant responses until the next user input.
 * 
 * Note: In our data model, SessionInput contains both user_prompt
 * and AI response data. An input with user_prompt is a "user turn",
 * and subsequent inputs without user_prompt (but with raw_llm_response)
 * are assistant responses.
 */
export function groupByConversationSection(inputs: SessionInput[]): ConversationSection[] {
  const sections: ConversationSection[] = [];
  let currentSection: { userInput: SessionInput; assistantResponses: SessionInput[] } | null = null;
  let sectionId = 0;

  for (const input of inputs) {
    // Summary input: treat as its own atomic section
    if (input.is_summary) {
      // Close previous section if exists
      if (currentSection !== null) {
        sections.push({
          id: sectionId++,
          userInput: currentSection.userInput,
          assistantResponses: currentSection.assistantResponses,
        });
        currentSection = null;
      }
      sections.push({
        id: sectionId++,
        userInput: input,
        assistantResponses: [],
        isSummary: true,
      });
      continue;
    }

    // User input: has a user_prompt
    if (input.user_prompt && input.user_prompt.trim()) {
      // Close previous section if exists
      if (currentSection !== null) {
        sections.push({
          id: sectionId++,
          userInput: currentSection.userInput,
          assistantResponses: currentSection.assistantResponses,
        });
      }
      currentSection = { userInput: input, assistantResponses: [] };
    } 
    // AI response: follows a user input, has raw_llm_response or aiActions
    else if (currentSection !== null && (input.raw_llm_response || input.aiActions?.length)) {
      currentSection.assistantResponses.push(input);
    }
    // Edge case: first input is an AI response (no preceding user prompt)
    // Create a synthetic section with empty user input
    else if (currentSection === null && (input.raw_llm_response || input.aiActions?.length)) {
      // Create synthetic user input for orphaned AI response
      const syntheticUserInput: SessionInput = {
        ...input,
        user_prompt: '',
        ad_hoc_context_definition: null,
      };
      currentSection = { userInput: syntheticUserInput, assistantResponses: [input] };
    }
  }

  // Close final section
  if (currentSection !== null) {
    sections.push({
      id: sectionId++,
      userInput: currentSection.userInput,
      assistantResponses: currentSection.assistantResponses,
    });
  }

  return sections;
}

/**
 * Truncates text from the start for preview display.
 */
export function truncateText(text: string, maxLength: number = 60): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.slice(0, maxLength - 3) + '...';
}

/**
 * Truncates text from the end for preview display.
 */
export function truncateTextFromEnd(text: string, maxLength: number = 60): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return '...' + trimmed.slice(-(maxLength - 3));
}

/**
 * Formats a collapsed preview string for an AI action.
 * Returns the most relevant argument value for display based on action type.
 */
export function formatCollapsedActionPreview(action: AIAction): string {
  const actionType = action.action_type;
  
  // Map action types to their primary preview fields
  switch (actionType) {
    case 'create_file':
    case 'overwrite_file':
    case 'delete_file':
    case 'quick_edit':
      if (action.file_path) {
        return action.file_path; // Show full path
      }
      return '';
    
    case 'run_command':
      if (action.command_string) {
        return action.command_string; // Show full command
      }
      return '';
    
    case 'request_context':
      if (action.files) {
        const items = action.files.split(',').map(f => f.trim()).filter(f => f);
        return items.join(', '); // Show all files
      }
      if (action.folders) {
        const items = action.folders.split(',').map(f => f.trim()).filter(f => f);
        return items.join(', '); // Show all folders
      }
      return '';
    
    case 'final':
      if (action.plain) {
        return action.plain; // Show full text
      }
      return '';
    
    case 'execute_code':
      return 'code block';
    
    case 'ask_user':
      if (action.selections) {
        try {
          const groups = JSON.parse(action.selections);
          if (Array.isArray(groups) && groups.length > 0 && groups[0].question) {
            return groups[0].question;
          }
        } catch {
          return action.selections.substring(0, 60);
        }
      }
      return 'Waiting for response';
    
    case 'invoke_subagent':
      if (action.arguments) {
        try {
          const args = JSON.parse(action.arguments);
          if (args.agent_name) {
            return args.agent_name;
          }
        } catch {}
      }
      return '';
    
    case 'replace_message_groups':
      return 'replace_message_groups';
    
    case 'list_mcp':
      return 'MCP server catalog';
    
    case 'list_tools':
      return 'MCP tool catalog';
    
    case 'load_tool': {
      if (action.server_name) {
        return action.server_name;
      }
      if (action.arguments) {
        try {
          const args = JSON.parse(action.arguments);
          if (args.server_name) return args.server_name;
        } catch {}
      }
      return 'load_tool';
    }
    
    default:
      // MCP tool actions have action_type pattern: serverName__toolName
      if (action.action_type.includes('__')) {
        return action.action_type;
      }
      if (action.file_path) {
        return action.file_path;
      }
      return '';
  }
}

